import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Bed, Bath, Maximize2, Calendar, Eye, MapPin, Share2, Map, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { NavBar } from '@/components/NavBar';
import { Footer } from '@/components/Footer';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { StickyMobileCTA } from '@/components/StickyMobileCTA';
import { LeadForm } from '@/components/LeadForm';
import { MeetingModal } from '@/components/MeetingModal';
import { PropertyGallery } from '@/components/PropertyGallery';
import { FloorPlanViewer } from '@/components/FloorPlanViewer';
import { Matterport3DViewer } from '@/components/Matterport3DViewer';
import { Kuula3DViewer } from '@/components/Kuula3DViewer';
import { UnitHero } from '@/components/UnitHero';
import { UnitCTA } from '@/components/UnitCTA';
import { ScrollToTopButton } from '@/components/ScrollToTopButton';
import { UnitDetailModal } from '@/components/UnitDetailModal';
import { PriceOnRequestCard } from '@/components/PriceOnRequestCard';
import { UnitLocationMap } from '@/components/UnitLocationMap';
import { RenderGallery } from '@/components/RenderGallery';
import { UnitNavigation } from '@/components/UnitNavigation';
import { PlansDisclaimer } from '@/components/PlansDisclaimer';
import { getUnitByNumber, getAllUnits, type PropertyUnit } from '@/lib/units';
import { getArchitectureDrawingForUnit, type ProcessedArchitectureDrawing } from '@/lib/architecture-drawings';
import type { Tables } from '@/integrations/supabase/types';

// Extended type that includes floor_plan_images from database
type UnitWithFloorPlans = PropertyUnit & {
  floor_plan_images?: string[] | null;
};
import { useUnits } from '@/hooks/useUnits';
import { trackEvent } from '@/lib/analytics';
import { formatCurrency, formatFloorDisplay, getStatusBadgeClasses } from '@/lib/utils';
import { trackUnitView } from '@/lib/unit-interest';

export const UnitDetail = () => {
  const { 'unit-number': unitNumber } = useParams<{ 'unit-number': string }>();
  const navigate = useNavigate();
  const [unit, setUnit] = useState<PropertyUnit | null>(null);
  const [relatedUnits, setRelatedUnits] = useState<PropertyUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [unitDetailOpen, setUnitDetailOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<PropertyUnit | null>(null);
  const [architectureDrawing, setArchitectureDrawing] = useState<ProcessedArchitectureDrawing | null>(null);

  // Load all units for Similar Units section and UnitNavigation
  const { units: allUnits } = useUnits({ autoLoad: true });

  // WhatsApp number - in production this would come from env variable
  const whatsappNumber = '13451234567';
  const whatsappMessage = encodeURIComponent(
    unit ? `Hi! I'm interested in unit ${unit.unit_number} at The Pearl at Grand Harbour.` : 
    "Hi! I'm interested in learning more about The Pearl at Grand Harbour properties."
  );

  // Load unit data
  useEffect(() => {
    const loadUnit = async () => {
      if (!unitNumber) {
        navigate('/residences');
        return;
      }

      setLoading(true);
      try {
        const { data, error } = await getUnitByNumber(unitNumber);
        
        if (error || !data) {
          console.error('Error loading unit:', error);
          navigate('/residences');
          return;
        }

        setUnit(data);

        // Load architecture drawing based on unit_type
        if (data.unit_type) {
          const archDrawing = await getArchitectureDrawingForUnit(data);
          setArchitectureDrawing(archDrawing);
        }

        // Load related units (same category, different unit)
        const { data: allUnitsData } = await getAllUnits({ category: data.category });
        const related = (allUnitsData || [])
          .filter(u => u.id !== data.id && u.availability === 'available')
          .slice(0, 3);
        setRelatedUnits(related);

        // Track unit view for lead qualification
        // Try to get contact email from sessionStorage (if they've submitted a form before)
        const contactEmail = typeof window !== 'undefined' 
          ? sessionStorage.getItem('hubspot_contact_email') || undefined
          : undefined;
        
        await trackUnitView(data.id, contactEmail);

        // Track page view (analytics)
        trackEvent('residences_unit_detail_view', { 
          unit_number: data.unit_number,
          category: data.category,
          view_type: data.view_type
        });

        // Set page title
        document.title = `Unit ${data.unit_number} - The Pearl at Grand Harbour`;
      } catch (error) {
        console.error('Error loading unit:', error);
        navigate('/residences');
      } finally {
        setLoading(false);
      }
    };

    loadUnit();
  }, [unitNumber, navigate]);

  const handleBackClick = () => {
    navigate('/residences');
    trackEvent('property_page_back', { unit: unit?.unit_number });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Unit ${unit?.unit_number} - The Pearl`,
        text: `Check out this luxury unit at The Pearl at Grand Harbour`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
    trackEvent('unit_page_share', { unit_number: unit?.unit_number });
  };


  const handleTabChange = (value: string) => {
    setActiveTab(value);
    trackEvent('unit_page_tab_change', { 
      unit_number: unit?.unit_number, 
      tab: value 
    });
  };

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
    trackEvent('whatsapp_click', { 
      location: 'unit_detail',
      unit: unit?.unit_number 
    });
  };

  const handleSitePlanNavigate = () => {
    if (!unit) return;
    const url = `/site-plan?unit=${encodeURIComponent(unit.unit_number)}`;
    trackEvent('unit_detail_site_plan_navigate', {
      unit_number: unit.unit_number,
      destination: '/site-plan',
    });
    navigate(url);
  };

  // Calculate previous and next units for navigation
  const { previousUnit, nextUnit } = useMemo(() => {
    if (!unit || !allUnits || allUnits.length === 0) {
      return { previousUnit: null, nextUnit: null };
    }

    // Filter to only available, published units and sort by unit_number
    const availableUnits = allUnits
      .filter(u => u.availability === 'available' && u.is_published)
      .sort((a, b) => a.unit_number.localeCompare(b.unit_number));

    if (availableUnits.length <= 1) {
      return { previousUnit: null, nextUnit: null };
    }

    // Find current unit index
    const currentIndex = availableUnits.findIndex(u => u.id === unit.id);

    if (currentIndex === -1) {
      return { previousUnit: null, nextUnit: null };
    }

    const previous = currentIndex > 0 ? availableUnits[currentIndex - 1] : null;
    const next = currentIndex < availableUnits.length - 1 ? availableUnits[currentIndex + 1] : null;

    return { previousUnit: previous, nextUnit: next };
  }, [unit, allUnits]);

  // Handle unit navigation
  const handleUnitNavigate = (targetUnitNumber: string) => {
    if (!unit) return;

    // Track analytics
    const isPrevious = previousUnit?.unit_number === targetUnitNumber;
    trackEvent(isPrevious ? 'unit_navigation_previous' : 'unit_navigation_next', {
      current_unit: unit.unit_number,
      target_unit: targetUnitNumber,
    });

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Navigate to new unit
    navigate(`/residences/${targetUnitNumber}`);
  };

  const formatArea = (area: number | null) => {
    if (!area) return 'N/A';
    return `${area.toLocaleString()} sq.ft`;
  };

  // Generate unit description
  const getUnitDescription = () => {
    if (!unit) return '';
    const categoryLabel = unit.category === '1bed' ? '1-bedroom' : 
                         unit.category === '2bed' ? '2-bedroom' : 
                         unit.category === 'townhouse' ? 'townhouse' : unit.category;
    const viewLabel = unit.view_type === 'lagoon' ? 'lagoon' : 'canal';
    return `This ${categoryLabel} unit offers luxury living with stunning ${viewLabel} views. Perfect for those seeking a premium lifestyle at The Pearl at Grand Harbour.`;
  };

  // Generate key features
  const getKeyFeatures = () => {
    if (!unit) return [];
    const features = [];
    if (unit.bedrooms > 0) features.push(`${unit.bedrooms} bedroom${unit.bedrooms > 1 ? 's' : ''}`);
    if (unit.bathrooms > 0) features.push(`${unit.bathrooms} bathroom${unit.bathrooms > 1 ? 's' : ''}`);
    if (unit.powder_rooms > 0) features.push(`${unit.powder_rooms} powder room${unit.powder_rooms > 1 ? 's' : ''}`);
    if (unit.study_room) features.push('Study room');
    if (unit.parking_spaces !== null && unit.parking_spaces > 0) features.push(`${unit.parking_spaces} parking space${unit.parking_spaces > 1 ? 's' : ''}`);
    if (unit.patio !== null && unit.patio > 0) features.push(`${unit.patio.toLocaleString()} sq ft patio`);
    if (unit.area_total) features.push(`${unit.area_total.toLocaleString()} sq ft total area`);
    if (unit.view_type === 'lagoon') features.push('Lagoon View');
    if (unit.view_type === 'canal') features.push('Canal View');
    return features;
  };

  // Generate highlights
  const getHighlights = () => {
    if (!unit) return [];
    const highlights = [];
    if (unit.view_type === 'lagoon') highlights.push('Lagoon View');
    if (unit.view_type === 'canal') highlights.push('Canal View');
    if (unit.study_room) highlights.push('Study Room');
    if (unit.powder_rooms > 0) highlights.push('Powder Room');
    highlights.push(formatFloorDisplay(unit.floor));
    return highlights;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavBar 
          onRegisterClick={() => setLeadFormOpen(true)} 
          onConsultClick={() => setMeetingOpen(true)}
        />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading unit details...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!unit) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <NavBar 
        onRegisterClick={() => setLeadFormOpen(true)} 
        onConsultClick={() => setMeetingOpen(true)}
      />

      {/* Hero Section */}
      <UnitHero unit={unit} />

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Property Actions */}
          <div className="flex items-center justify-between mb-8">
            <Button
              variant="ghost"
              onClick={handleBackClick}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Residences
            </Button>
            
            <div className="flex items-center gap-2">
              <Button
                size="lg"
                onClick={handleSitePlanNavigate}
                className="gap-2 bg-brand-teal hover:bg-brand-teal/90 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                <Map className="w-5 h-5" />
                View Site Plan
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShare}
                className="gap-2"
              >
                <Share2 className="w-4 h-4" />
                Share
              </Button>
            </div>
          </div>

          {/* Unit Info Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <h1 className="font-display text-3xl lg:text-4xl font-bold text-foreground mb-2">
                  Unit {unit.unit_number}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
                  {unit.area_total && (
                    <>
                      <span>{unit.area_total.toLocaleString()} sq ft</span>
                      <span>•</span>
                    </>
                  )}
                  <span>{unit.bedrooms} bed{unit.bedrooms > 1 ? 's' : ''}</span>
                  <span>•</span>
                  <span>{unit.bathrooms} bath{unit.bathrooms > 1 ? 's' : ''}</span>
                  {unit.show_price && unit.price ? (
                    <>
                      <span>•</span>
                      <Badge variant="outline" className="text-primary border-primary">
                        {formatCurrency(unit.price)}
                      </Badge>
                    </>
                  ) : (
                    <>
                      <span>•</span>
                      <Badge variant="outline" className="text-brand-teal border-brand-teal bg-brand-teal/10">
                        💎 Price on Request
                      </Badge>
                    </>
                  )}
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" className="gap-2">
                  <Phone className="w-4 h-4" />
                  Call Now
                </Button>
                <Button className="gap-2" onClick={() => setLeadFormOpen(true)}>
                  <Mail className="w-4 h-4" />
                  Register Interest
                </Button>
              </div>
            </div>

            {/* Prominent CTA Buttons */}
            <div className="flex flex-wrap gap-4 mb-8">
              <Button
                size="lg"
                className="bg-brand-teal hover:bg-brand-teal/90 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                onClick={handleSitePlanNavigate}
              >
                <Map className="w-5 h-5 mr-2" />
                View Site Plan
              </Button>
              <Button
                size="lg"
                className="bg-brand-teal hover:bg-brand-teal/90 text-white"
                onClick={() => setLeadFormOpen(true)}
              >
                <Calendar className="w-5 h-5 mr-2" />
                Register Interest
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => setMeetingOpen(true)}
              >
                <Eye className="w-5 h-5 mr-2" />
                Schedule Consultation
              </Button>
            </div>

            {/* Badges Display */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <Badge variant="default" className="bg-brand-teal text-white">
                {unit.category}
              </Badge>
              <Badge 
                className={getStatusBadgeClasses(unit.availability)}
              >
                {unit.availability.charAt(0).toUpperCase() + unit.availability.slice(1)}
              </Badge>
              <Badge variant="outline">
                {unit.view_type === 'lagoon' ? 'Lagoon View' : 'Canal View'}
              </Badge>
              <Badge variant="outline">
                {formatFloorDisplay(unit.floor)}
              </Badge>
            </div>

            {/* Price on Request Card (only show if price is hidden) */}
            {!unit.show_price && (
              <div className="mb-8">
                <PriceOnRequestCard
                  variant="expanded"
                  onRequestClick={() => {
                    trackEvent('price_inquiry', {
                      source: 'unit_detail_page',
                      unit_number: unit.unit_number,
                      inquiry_type: 'hidden_price'
                    });
                    setLeadFormOpen(true);
                  }}
                />
              </div>
            )}
          </div>

          {/* 3D Renders Gallery */}
          {unit.render_images && unit.render_images.length > 0 && (
            <RenderGallery 
              images={unit.render_images} 
              unitNumber={unit.unit_number} 
              rotationAngle={unit.render_rotation_angle ?? 0}
            />
          )}

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-8">
            <TabsList className="flex flex-wrap w-full gap-2 sm:gap-3 justify-center lg:grid lg:grid-cols-3 mb-10 rounded-xl bg-muted p-1">
              <TabsTrigger value="overview" className="text-sm flex-none min-w-0 whitespace-normal">Overview</TabsTrigger>
              <TabsTrigger value="gallery" className="text-sm flex-none min-w-0 whitespace-normal">Gallery</TabsTrigger>
              <TabsTrigger value="floorplan" className="text-sm flex-none min-w-0 whitespace-normal">Floor Plan</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-8">
              {/* Description */}
              <Card>
                <CardContent className="p-8">
                  <h2 className="font-display text-2xl font-semibold mb-4">About This Unit</h2>
                  
                  {/* Unit Location Map - floats right on desktop, stacks on mobile */}
                  <div className="md:float-right md:ml-6 md:mb-4 mb-6 md:w-64 lg:w-80">
                    <UnitLocationMap 
                      unitNumber={unit.unit_number}
                      showTitle
                      compact
                    />
                  </div>
                  
                  <p className="text-muted-foreground leading-relaxed mb-6 text-lg">
                    {getUnitDescription()}
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-8 clear-both">
                    {/* Key Features */}
                    <div>
                      <h3 className="font-semibold text-lg mb-4">Key Features</h3>
                      <ul className="space-y-3">
                        {getKeyFeatures().map((feature, index) => (
                          <li key={index} className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Unit Details */}
                    <div>
                      <h3 className="font-semibold text-lg mb-4">Unit Details</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">View:</span>
                          <span className="font-medium">{unit.view_type === 'lagoon' ? 'Lagoon View' : 'Canal View'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Floor:</span>
                          <span className="font-medium">{formatFloorDisplay(unit.floor)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Category:</span>
                          <span className="font-medium">{unit.category}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Availability:</span>
                          <span className="font-medium">{unit.availability.charAt(0).toUpperCase() + unit.availability.slice(1)}</span>
                        </div>
                        {unit.study_room && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Study Room:</span>
                            <span className="font-medium">Yes</span>
                          </div>
                        )}
                        {unit.parking_spaces !== null && unit.parking_spaces > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Parking Spaces:</span>
                            <span className="font-medium">{unit.parking_spaces} space{unit.parking_spaces > 1 ? 's' : ''}</span>
                          </div>
                        )}
                        {unit.patio !== null && unit.patio > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Patio:</span>
                            <span className="font-medium">{unit.patio.toLocaleString()} sq ft</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Area Breakdown */}
                  {(unit.area_internal || unit.area_external_covered || unit.area_external_uncovered) && (
                    <div className="mt-8">
                      <h3 className="font-semibold text-lg mb-4">Area Breakdown</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {unit.area_internal && (
                          <div className="p-4 bg-muted rounded-lg">
                            <div className="text-sm text-muted-foreground mb-2">Internal Area</div>
                            <div className="text-2xl font-bold">{formatArea(unit.area_internal)}</div>
                          </div>
                        )}
                        {unit.area_external_covered && (
                          <div className="p-4 bg-muted rounded-lg">
                            <div className="text-sm text-muted-foreground mb-2">External Covered</div>
                            <div className="text-2xl font-bold">{formatArea(unit.area_external_covered)}</div>
                          </div>
                        )}
                        {unit.area_external_uncovered && (
                          <div className="p-4 bg-muted rounded-lg">
                            <div className="text-sm text-muted-foreground mb-2">External Uncovered</div>
                            <div className="text-2xl font-bold">{formatArea(unit.area_external_uncovered)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Highlights */}
                  <div className="mt-8">
                    <h3 className="font-semibold text-lg mb-4">Highlights</h3>
                    <div className="flex flex-wrap gap-3">
                      {getHighlights().map((highlight, index) => (
                        <Badge key={index} variant="secondary" className="text-sm">
                          {highlight}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* 3D Virtual Tour */}
              {(unit.kuula_url || unit.matterport_url) && (
                <Card>
                  <CardContent className="p-8">
                    <h2 className="font-display text-2xl font-semibold mb-4">3D Virtual Tour</h2>
                    {unit.kuula_url ? (
                      <Kuula3DViewer kuulaUrl={unit.kuula_url} unitNumber={unit.unit_number} />
                    ) : unit.matterport_url ? (
                      <Matterport3DViewer matterportUrl={unit.matterport_url} propertyName={unit.unit_number} />
                    ) : null}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="gallery">
              {unit.images && unit.images.length > 0 ? (
                <PropertyGallery images={unit.images} propertyName={`Unit ${unit.unit_number}`} />
              ) : (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No images available for this unit yet.</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="floorplan">
              <FloorPlanViewer 
                architectureDrawing={architectureDrawing}
                uploadedFloorPlans={(unit as UnitWithFloorPlans).floor_plan_images || []}
                propertyName={`Unit ${unit.unit_number}`}
              />
            </TabsContent>
          </Tabs>

          {/* Disclaimer - shown once per unit detail page */}
          <PlansDisclaimer className="mt-8" />
        </div>
      </main>

      {/* Interior Design Customising Section */}
      <section className="py-16 bg-background border-t border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Content Side */}
              <div className="space-y-6">
                <h2 className="font-display text-3xl lg:text-4xl font-semibold text-foreground">
                  Interior Design Customising
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Personalize your residence with our bespoke interior design services. 
                  Work with our design team to create a space that reflects your unique style and preferences.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                    <span>Custom finishes and materials</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                    <span>Personalized color schemes</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                    <span>Furniture and fixture selection</span>
                  </li>
                </ul>
                <Button
                  size="lg"
                  className="bg-brand-teal hover:bg-brand-teal/90 text-white"
                  onClick={() => {
                    trackEvent('interior_design_cta_click', { 
                      unit_number: unit?.unit_number,
                      location: 'unit_detail'
                    });
                    // External URL - leave blank for now, will be configured later
                    const interiorDesignUrl = ''; // TODO: Add external URL
                    if (interiorDesignUrl) {
                      window.open(interiorDesignUrl, '_blank');
                    }
                  }}
                >
                  Explore Interior Design Options
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              
              {/* Visual Side */}
              <div className="relative overflow-hidden rounded-2xl shadow-luxury">
                <img
                  src="/img/residence-interior.jpg"
                  alt="Interior design customization"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Similar Units Section */}
      {relatedUnits.length > 0 && (
        <section className="py-16 bg-soft-cream">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-display text-3xl lg:text-4xl font-semibold text-foreground mb-4">
                  Similar Units
                </h2>
                <p className="text-lg text-muted-foreground">
                  Explore other available units in the same category
                </p>
              </div>

              <div className="grid lg:grid-cols-3 gap-8">
                {relatedUnits.map((relatedUnit) => (
                  <div
                    key={relatedUnit.id}
                    className="relative group bg-background rounded-lg border overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/residences/${relatedUnit.unit_number}`)}
                  >
                    {/* Unit Image */}
                    <div className="relative h-48 overflow-hidden bg-muted">
                      {relatedUnit.images && relatedUnit.images.length > 0 ? (
                        <img
                          src={relatedUnit.images[0]}
                          alt={`Unit ${relatedUnit.unit_number}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/img/residence-interior.jpg';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          <MapPin className="w-12 h-12" />
                        </div>
                      )}
                      <div className="absolute top-4 right-4">
                        <Badge variant="outline" className="bg-background/90 backdrop-blur-sm">
                          {relatedUnit.category}
                        </Badge>
                      </div>
                    </div>

                    {/* Unit Info */}
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display text-2xl font-bold group-hover:text-brand-teal transition-colors duration-300">
                          Unit {relatedUnit.unit_number}
                        </h3>
                        <Badge 
                          className={getStatusBadgeClasses(relatedUnit.availability)}
                        >
                          {relatedUnit.availability.charAt(0).toUpperCase() + relatedUnit.availability.slice(1)}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-muted-foreground mb-4">
                        <span>{relatedUnit.bedrooms} bed{relatedUnit.bedrooms > 1 ? 's' : ''}</span>
                        <span>•</span>
                        <span>{relatedUnit.bathrooms} bath{relatedUnit.bathrooms > 1 ? 's' : ''}</span>
                        {relatedUnit.area_total && (
                          <>
                            <span>•</span>
                            <span>{relatedUnit.area_total.toLocaleString()} sq ft</span>
                          </>
                        )}
                      </div>
                      {relatedUnit.show_price && relatedUnit.price && (
                        <div className="mb-4">
                          <Badge variant="outline" className="text-primary border-primary">
                            {formatCurrency(relatedUnit.price)}
                          </Badge>
                        </div>
                      )}
                      <Button
                        size="sm"
                        className="w-full bg-brand-teal hover:bg-brand-teal/90 text-white hover:scale-105 transition-all duration-300"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/residences/${relatedUnit.unit_number}`);
                        }}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Unit Details
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Sticky CTA */}
      <UnitCTA unit={unit} />

      {/* Unit Navigation */}
      <UnitNavigation
        previousUnit={previousUnit}
        nextUnit={nextUnit}
        onNavigate={handleUnitNavigate}
      />

      {/* Footer */}
      <Footer />

      {/* Persistent Conversion UI */}
      <StickyMobileCTA onClick={() => setLeadFormOpen(true)} />
      <WhatsAppButton onClick={handleWhatsAppClick} />
      <ScrollToTopButton />

      {/* Modals */}
      <UnitDetailModal
        open={unitDetailOpen}
        onOpenChange={setUnitDetailOpen}
        unit={selectedUnit}
        onRegisterClick={() => {
          setUnitDetailOpen(false);
          setLeadFormOpen(true);
        }}
        onConsultClick={() => {
          setUnitDetailOpen(false);
          setMeetingOpen(true);
        }}
      />
      <LeadForm 
        open={leadFormOpen} 
        onOpenChange={setLeadFormOpen}
        unitNumber={unit?.unit_number}
      />
      <MeetingModal
        open={meetingOpen}
        onOpenChange={setMeetingOpen}
        onWhatsAppClick={handleWhatsAppClick}
        unitNumber={unit.unit_number}
      />
    </div>
  );
};
